import { Link, useNavigate } from 'react-router-dom';
import { Aperture, LogOut, Settings } from 'lucide-react';
import { useLogout } from '@/hooks/useAuth';
import { useAuthStore } from '@/store/authStore';
import { useCompetitions, useMyCompetitions } from '@/hooks/useCompetitions';
import { isSuperAdmin } from '@/lib/roles';
import { competitionStatusLabel, competitionStatusTone, isMine } from '@/lib/competitions';
import { describeDeadline } from '@/lib/dates';
import { getApiErrorMessage } from '@/api/client';
import { CompetitionStatus } from '@/api/types';
import type { Competition, MyCompetition } from '@/api/types';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { PageSpinner } from '@/components/ui/Spinner';
import { EmptyState, ErrorState } from '@/components/ui/Alert';

interface PickerItem {
  id: string;
  slug: string;
  name: string;
  status: CompetitionStatus;
  organizationName?: string | null;
  logoUrl?: string | null;
  deadline?: string | null;
  roles: string[];
}

function rolesOf(m: MyCompetition | undefined, admin: boolean): string[] {
  if (admin) return ['Skrbnik sistema'];
  if (!m) return [];
  return [
    m.canManage && 'Organizator',
    m.isJudge && 'Sodnik',
    m.isParticipant && 'Tekmovalec',
  ].filter((r): r is string => Boolean(r));
}

/** Deadline line: only while entries can still be submitted. */
function deadlineText(item: PickerItem): string | null {
  if (item.status !== CompetitionStatus.Active || !item.deadline) return null;
  return `Rok za oddajo · ${describeDeadline(item.deadline)}`;
}

function CompetitionCard({ item, onOpen }: { item: PickerItem; onOpen: (id: string) => void }) {
  const deadline = deadlineText(item);
  return (
    <li className="w-40 shrink-0 snap-center first:ml-auto last:mr-auto sm:w-56">
      <button
        type="button"
        onClick={() => onOpen(item.id)}
        className="group flex w-full flex-col items-center gap-4 text-center"
      >
        <span className="flex aspect-square w-full items-center justify-center rounded-card border border-line-strong bg-surface-1 p-6 shadow-card outline-2 outline-offset-4 outline-transparent transition-colors duration-fast group-hover:bg-surface-2 group-hover:outline-accent group-focus-visible:outline-accent">
          {item.logoUrl ? (
            <img src={item.logoUrl} alt="" className="max-h-full max-w-full object-contain" />
          ) : (
            <span className="font-display text-7xl font-semibold tracking-tight text-fg-muted" aria-hidden="true">
              {item.name.slice(0, 1).toUpperCase()}
            </span>
          )}
        </span>
        <span className="flex flex-col items-center gap-1.5">
          <span className="line-clamp-2 text-base font-semibold text-fg-secondary transition-colors duration-fast group-hover:text-fg">
            {item.name}
          </span>
          {item.roles.length > 0 && (
            <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-fg-muted">
              {item.roles.join(' · ')}
            </span>
          )}
          <Badge tone={competitionStatusTone(item.status)}>{competitionStatusLabel(item.status)}</Badge>
          {deadline && <span className="text-xs text-fg-muted">{deadline}</span>}
        </span>
      </button>
    </li>
  );
}

/**
 * Where every sign-in lands, and where the user comes back to switch
 * competition. A person registers once and holds a role per competition; they
 * see only the competitions they belong to (a SuperAdmin sees all of them).
 * Choosing a card opens that competition and everything after is scoped to it.
 */
export function CompetitionPickerPage() {
  const user = useAuthStore((s) => s.user);
  const setActiveCompetitionId = useAuthStore((s) => s.setActiveCompetitionId);
  const navigate = useNavigate();
  const logout = useLogout();
  const admin = isSuperAdmin(user);

  const mine = useMyCompetitions();
  // Names the user may not have in their own list (logo, deadline) come from the full list.
  const all = useCompetitions({ page: 1, pageSize: 100 });

  const mineList = mine.data?.items ?? [];
  const fullById = new Map<string, Competition>((all.data?.items ?? []).map((c) => [c.id, c]));

  const items: PickerItem[] = admin
    ? (all.data?.items ?? []).map((c) => ({
        id: c.id,
        slug: c.slug,
        name: c.name,
        status: c.status,
        organizationName: c.organizationName,
        logoUrl: c.logoUrl ?? c.organizationLogoUrl,
        deadline: c.submissionDeadline,
        roles: rolesOf(undefined, true),
      }))
    : mineList.filter(isMine).map((m) => {
        const full = fullById.get(m.competitionId);
        return {
          id: m.competitionId,
          slug: m.slug,
          name: m.competitionName,
          status: m.status,
          organizationName: m.organizationName ?? full?.organizationName,
          logoUrl: m.logoUrl ?? full?.logoUrl ?? full?.organizationLogoUrl,
          deadline: full?.submissionDeadline,
          roles: rolesOf(m, false),
        };
      });

  const loading = admin ? all.isLoading : mine.isLoading;
  const error = admin ? all.error : mine.error;
  const canManageAny = admin || mineList.some((c) => c.canManage);

  // Grouped by what the person does there; a competition lands in the group of their highest role.
  const mineById = new Map(mineList.map((m) => [m.competitionId, m]));
  const groups: { title: string; items: PickerItem[] }[] = admin
    ? [{ title: 'Vsa tekmovanja', items }]
    : [
        { title: 'Organiziram', items: items.filter((i) => mineById.get(i.id)?.canManage) },
        { title: 'Ocenjujem', items: items.filter((i) => !mineById.get(i.id)?.canManage && mineById.get(i.id)?.isJudge) },
        {
          title: 'Sodelujem',
          items: items.filter((i) => !mineById.get(i.id)?.canManage && !mineById.get(i.id)?.isJudge),
        },
      ].filter((g) => g.items.length > 0);

  function open(id: string) {
    setActiveCompetitionId(id);
    navigate(`/competitions/${items.find((i) => i.id === id)?.slug ?? id}`);
  }

  async function handleLogout() {
    await logout.mutateAsync().catch(() => undefined);
    navigate('/login', { replace: true });
  }

  const firstName = user?.displayName?.split(/\s+/)[0];

  return (
    <div className="flex min-h-screen flex-col bg-surface-0 text-fg">
      <header className="flex h-14 items-center justify-between px-4 md:px-8">
        <span className="flex items-center gap-2.5">
          <Aperture size={20} aria-hidden="true" />
          <span className="font-display text-lg font-semibold tracking-tight">Zaslonka</span>
        </span>
        <div className="flex items-center gap-1">
          <Link
            to="/profile"
            className="inline-flex h-10 items-center rounded-control px-3 text-sm font-medium text-fg-secondary transition-colors duration-fast hover:bg-surface-2 hover:text-fg"
          >
            {user?.displayName || user?.email}
          </Link>
          <Button variant="ghost" size="sm" onClick={handleLogout} isLoading={logout.isPending}>
            <LogOut size={16} aria-hidden="true" />
            <span className="hidden sm:inline">Odjava</span>
          </Button>
        </div>
      </header>

      <main className="flex flex-1 flex-col items-center justify-center gap-12 py-10">
        <div className="px-4 text-center">
          <h1 className="font-display text-4xl font-semibold leading-[1.05] tracking-tight md:text-5xl">
            {items.length > 1 ? 'Katero tekmovanje?' : firstName ? `Pozdravljeni, ${firstName}` : 'Pozdravljeni'}
          </h1>
          <p className="mt-3 text-sm text-fg-secondary">
            {items.length > 1 ? 'Izberite tekmovanje, v katerem želite delati.' : 'Vstopite v svoje tekmovanje.'}
          </p>
        </div>

        {loading && <PageSpinner />}
        {error && (
          <div className="w-full max-w-xl px-4">
            <ErrorState message={getApiErrorMessage(error)} onRetry={() => (admin ? all.refetch() : mine.refetch())} />
          </div>
        )}
        {!loading && !error && items.length === 0 && (
          <div className="w-full max-w-xl px-4">
            <EmptyState
              title="Niste vključeni v nobeno tekmovanje"
              description="Prijavite se prek povezave na tekmovanje ali počakajte, da vas organizator doda. Tekmovanje se bo prikazalo tukaj."
            />
          </div>
        )}

        {groups.map((group) => (
          <section key={group.title} className="flex w-full flex-col gap-2">
            {groups.length > 1 && (
              <h2 className="px-6 text-center font-mono text-xs uppercase tracking-[0.08em] text-fg-muted md:px-12">{group.title}</h2>
            )}
            <ul
              aria-label={group.title}
              className="flex w-full snap-x snap-mandatory gap-6 overflow-x-auto scroll-smooth px-6 py-4 sm:gap-10 md:px-12"
            >
              {group.items.map((item) => (
                <CompetitionCard key={item.id} item={item} onOpen={open} />
              ))}
            </ul>
          </section>
        ))}

        {canManageAny && (
          <Link to="/organizer/competitions">
            <Button variant="secondary">
              <Settings size={16} aria-hidden="true" />
              Vsa tekmovanja
            </Button>
          </Link>
        )}
      </main>
    </div>
  );
}
